import { useEffect, useState } from "react";

import { Icon, useHotkeys } from "@blueprintjs/core";
import styled from "@emotion/styled";

import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { FadeInBox } from "@/components/pieces/FadeInBox";
import { HighlightText } from "@/components/pieces/HighlightText";
import { CtaButton } from "@/components/pieces/onboarding/CtaButton";
import { PageTitle } from "@/components/pieces/onboarding/PageTitle";
import { BP_NS, SUBTEXT_COLOR } from "@/css/constants";
import { FetchFulfillmentFormsQuery } from "@/graphql";
import { ProgramFormField, SurveyStepComponentProps } from "@/types/surveys";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

type BaseFormFieldOption = NonNullableArrayItem<
  ProgramFormField["formFieldOptions"]
>;

type IncompleteFormFieldOption = NonNullable<
  NonNullable<
    NonNullableArrayItem<
      NonNullableArrayItem<
        NonNullable<
          FetchFulfillmentFormsQuery["order"]
        >["incompleteFulfillmentForms"]
      >["formFieldAnswers"]
    >
  >["optionValue"]
>;

type FormFieldOption = BaseFormFieldOption | IncompleteFormFieldOption;

interface RatingScaleProps {
  formFieldOptions: FormFieldOption[];
  value: string;
  onSelect: (value: string) => void;
  className?: string;
}

export function RatingScale({
  formFieldOptions,
  value,
  onSelect,
  className = "",
}: RatingScaleProps) {
  return (
    <RadioGroupStyled className={`radio-group ${className}`}>
      {formFieldOptions.map((item) => {
        const isSelected = value === item.id;
        return (
          <Flex
            key={item.id}
            className="radio-item"
            onClick={() => onSelect && onSelect(item.id)}
          >
            <Flex className="radio-label">{item.label}</Flex>
            <Box className="radio-icon">
              <Icon
                icon={isSelected ? "tick-circle" : "circle"}
                size={24}
                color={isSelected ? "#165A36" : "#8c9aa7"}
              />
            </Box>
          </Flex>
        );
      })}
    </RadioGroupStyled>
  );
}

export function RatingScaleField({
  field,
  answer,
  form,
  loading,
  save,
}: SurveyStepComponentProps) {
  const product = form.product ?? {
    name: "",
    slug: "",
  };

  const formFieldOptions = ensureNonNullArray(field.formFieldOptions);
  const optionValue = answer?.optionValue ? answer.optionValue.id : "";
  const [value, setValue] = useState<string>(optionValue);
  const submitDisabled = (field.required && !value) || loading;

  useEffect(() => {
    setValue(optionValue);
  }, [field]);

  function submit() {
    if (loading || submitDisabled) return;

    save({
      id: form.id,
      answerData: [{ fieldId: field.id, value }],
    });
  }

  useHotkeys([
    {
      combo: "enter",
      group: "Rating Scale",
      label: "Submit rating",
      onKeyDown: submit,
      allowInInput: true,
      global: true,
    },
  ]);

  return (
    <FadeInBox>
      <Flex justifyContent="center">
        <Box width={[1, 1, 670]}>
          {product?.name && (
            <Box mt={24}>
              For your{" "}
              <HighlightText key={product.slug}>{product.name}</HighlightText>
            </Box>
          )}
          <PageTitle title={field.prompt} subtitle={field.helpText}>
            {field.required ? <span style={{ color: "red" }}>*</span> : null}
          </PageTitle>
          <RatingScale
            formFieldOptions={formFieldOptions}
            value={value}
            onSelect={setValue}
          />
          <CtaButton
            disabled={submitDisabled}
            loading={loading}
            onClick={submit}
            visible={true}
          >
            Continue
          </CtaButton>
        </Box>
      </Flex>
    </FadeInBox>
  );
}

export const RadioGroupStyled = styled(Flex)`
  &.small-rating-scale {
    .radio-item {
      .radio-label {
        font-size: 14px;
      }
    }
  }
  width: 100%;
  .radio-item {
    flex: 1;
    flex-direction: column;
    align-items: center;
    .radio-label {
      flex: 1;
      font-size: 16px;
      align-items: flex-end;
      display: flex;
      text-align: center;
      color: ${SUBTEXT_COLOR};
      margin-bottom: 10px;
    }
    .radio-icon {
      background-color: #edeeee;
      width: 100%;
      text-align: center;
      padding: 8px 0px;
      border-top: solid 1px #e3e3e3;
      border-bottom: solid 1px #e3e3e3;
      .${BP_NS}-icon {
        background: white;
        border-radius: 50%;
      }
    }
    &:hover {
      cursor: pointer;
      .radio-icon {
        .${BP_NS}-icon {
          background: inherit;
        }
      }
    }
    &:first-of-type {
      .radio-icon {
        border-left: solid 1px #e3e3e3;
        border-radius: 5px;
        border-radius: 5px 0px 0px 5px;
      }
    }
    &:last-of-type {
      .radio-icon {
        border-right: solid 1px #e3e3e3;
        border-radius: 5px;
        border-radius: 0px 5px 5px 0px;
      }
    }
  }
  @media screen and (max-width: ${breakpoints.sm - 1}px) {
    flex-direction: column;
    .radio-item {
      flex-direction: row-reverse;
      .radio-icon {
        width: auto;
        background: none;
        border: none;
      }
      &:first-of-type {
        .radio-icon {
          border: none;
        }
      }
      &:last-of-type {
        .radio-icon {
          border: none;
        }
      }
      .radio-label {
        margin-left: 5px;
        margin-bottom: 0px;
      }
    }
  }
`;
